import { useState, useEffect } from "react";
import {
  Star,
  Send,
  Edit2,
  Trash2,
  Loader2,
  CheckCircle,
  Clock,
  XCircle,
  MessageSquareQuote,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { useAuth } from "@/contexts/AuthContext";
import { useConfirm } from "@/contexts/ConfirmContext";

interface Testimonial {
  id: string;
  authorName: string;
  businessName: string;
  content: string;
  rating: number;
  status: string;
  rejectionReason: string | null;
  createdAt: string;
  reviewedAt: string | null;
}

export default function MyTestimonial() {
  const { user } = useAuth();
  const { confirm } = useConfirm();
  const [testimonial, setTestimonial] = useState<Testimonial | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState("");
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  useEffect(() => {
    loadTestimonial();
  }, []);

  const loadTestimonial = async () => {
    try {
      const res = await apiClient.get("/testimonials/mine");
      setTestimonial(res.data.data);
      if (res.data.data) {
        setContent(res.data.data.content);
        setRating(res.data.data.rating);
      }
    } catch {
      // No testimonial yet
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!content.trim() || content.trim().length < 10) {
      setMessage({
        type: "error",
        text: "El testimonio debe tener al menos 10 caracteres.",
      });
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      if (testimonial) {
        const res = await apiClient.put("/testimonials/mine", {
          content: content.trim(),
          rating,
        });
        setTestimonial(res.data.data);
        setEditing(false);
        setMessage({ type: "success", text: res.data.message });
      } else {
        const res = await apiClient.post("/testimonials", {
          content: content.trim(),
          rating,
        });
        setTestimonial(res.data.data);
        setMessage({ type: "success", text: res.data.message });
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Error al enviar el testimonio",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!(await confirm({ title: "¿Eliminar tu testimonio?", message: "Esta acción no se puede deshacer.", confirmText: "Sí, eliminar", danger: true })))
      return;

    setSaving(true);
    try {
      await apiClient.delete("/testimonials/mine");
      setTestimonial(null);
      setContent("");
      setRating(5);
      setEditing(false);
      setMessage({ type: "success", text: "Testimonio eliminado" });
    } catch {
      setMessage({ type: "error", text: "Error al eliminar el testimonio" });
    } finally {
      setSaving(false);
    }
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
            <Clock className="w-3.5 h-3.5" />
            Pendiente de revisión
          </span>
        );
      case "approved":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3.5 h-3.5" />
            Publicado
          </span>
        );
      case "rejected":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <XCircle className="w-3.5 h-3.5" />
            Rechazado
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // Only admin (hostelero) can submit testimonials
  if (user?.role === "staff") {
    return (
      <div className="min-h-screen bg-gray-50 p-3 md:p-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <MessageSquareQuote className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Acceso restringido
            </h2>
            <p className="text-gray-500">
              Solo los hosteleros (administradores) pueden enviar testimonios.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            Mi Testimonio
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Comparte tu experiencia con ReservaManager. Tu valoración aparecerá
            en nuestra web una vez aprobada.
          </p>
        </div>

        {/* Message */}
        {message && (
          <div
            className={`mb-4 p-4 rounded-lg flex items-center gap-2 ${
              message.type === "success"
                ? "bg-green-50 text-green-800 border border-green-200"
                : "bg-red-50 text-red-800 border border-red-200"
            }`}
          >
            {message.type === "success" ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <XCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="text-sm">{message.text}</p>
          </div>
        )}

        {/* Existing testimonial view */}
        {testimonial && !editing ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                {statusBadge(testimonial.status)}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditing(true)}
                    className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                    title="Editar"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={saving}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-5 h-5 ${
                      star <= testimonial.rating
                        ? "text-yellow-400 fill-yellow-400"
                        : "text-gray-300"
                    }`}
                  />
                ))}
              </div>

              {/* Content */}
              <p className="text-gray-700 italic text-lg leading-relaxed">
                &ldquo;{testimonial.content}&rdquo;
              </p>

              {/* Author info */}
              <div className="mt-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-bold text-sm">
                  {testimonial.authorName
                    .split(" ")
                    .map((w) => w[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2)}
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">
                    {testimonial.authorName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {testimonial.businessName}
                  </p>
                </div>
              </div>

              {/* Rejection reason */}
              {testimonial.status === "rejected" &&
                testimonial.rejectionReason && (
                  <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-100">
                    <p className="text-sm text-red-700">
                      <strong>Motivo del rechazo:</strong>{" "}
                      {testimonial.rejectionReason}
                    </p>
                    <p className="text-xs text-red-500 mt-1">
                      Puedes editar tu testimonio y reenviarlo para revisión.
                    </p>
                  </div>
                )}
            </div>
          </div>
        ) : (
          /* Create / Edit form */
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {testimonial ? "Editar testimonio" : "Escribe tu valoración"}
              </h2>

              {/* Rating selector */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Puntuación
                </label>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="p-0.5 transition-transform hover:scale-110"
                    >
                      <Star
                        className={`w-8 h-8 transition-colors ${
                          star <= (hoverRating || rating)
                            ? "text-yellow-400 fill-yellow-400"
                            : "text-gray-300"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Content textarea */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tu experiencia con ReservaManager
                </label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={4}
                  maxLength={500}
                  placeholder="Cuéntanos cómo ReservaManager ha ayudado a tu negocio..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none text-gray-900"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">
                  {content.length}/500 caracteres
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSubmit}
                  disabled={saving || !content.trim()}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {testimonial ? "Actualizar y reenviar" : "Enviar testimonio"}
                </button>
                {editing && (
                  <button
                    onClick={() => {
                      setEditing(false);
                      setContent(testimonial?.content || "");
                      setRating(testimonial?.rating || 5);
                    }}
                    className="px-4 py-2.5 text-gray-600 font-medium rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Cancelar
                  </button>
                )}
              </div>

              <p className="mt-4 text-xs text-gray-400">
                Tu testimonio será revisado por nuestro equipo antes de
                publicarse en la web de ReservaManager.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
